<script setup lang="ts">
import { computed, ref, useSlots, useTemplateRef } from 'vue';

import type { PhxPictureProps } from '@sg-core/src/View/Components/Atoms/Picture';

import { PhxsgButton } from '../../Atoms/Button';
import { PhxsgIcon } from '../../Atoms/Icon';
import { PhxsgPicture } from '../../Atoms/Picture';
import { PhxsgTag } from '../../Atoms/Tag';
import { PhxsgSlide, PhxsgSlider } from '../../Organisms/Slider';

import type { Swiper as SwiperType } from 'swiper/types';

import '@sg-core/src/View/Components/Atoms/Text/text.css';

export interface PhxsgProductCardProps {
  medias?: PhxPictureProps[];
  enableSlider?: boolean;
  name: string;
  price?: string;
  productRef?: string;
  tags?: string[];
  horizontal?: boolean;
  horizontalOnly?: boolean;
  fullWidthMedia?: boolean;
  href?: string;
  target?: string;
  dataTracking?: string;
  previousSlideLabel?: string;
  nextSlideLabel?: string;
}

const {
  medias = [],
  enableSlider = true,
  name,
  price,
  productRef,
  tags = [],
  horizontal = false,
  horizontalOnly = false,
  fullWidthMedia = false,
  href,
  target,
  dataTracking,
  previousSlideLabel = 'Previous slide',
  nextSlideLabel = 'Next slide',
} = defineProps<PhxsgProductCardProps>();

const emit = defineEmits<(e: 'click') => void>();
const slots = useSlots();
const scrollbarTranslate = ref(0);
const linkRef = useTemplateRef<HTMLAnchorElement>('linkRef');

const pictureType = computed(() => (horizontal || horizontalOnly ? 'full' : 'contain'));

const hasMultipleMedias = computed(() => enableSlider && medias.length > 1);
const primaryMedia = computed(() => medias[0] ?? null);
const hasTags = computed(() => tags.length > 0 || !!slots.tags);
const hasPriceContent = computed(() => !!price || !!slots.price);
const hasReferenceContent = computed(() => !!productRef || !!slots.reference);
const hasActionsContent = computed(() => !!slots.actions);

const linkAttrs = computed(() => ({
  ...(target ? { target } : {}),
  ...(dataTracking ? { 'data-tracking': dataTracking } : {}),
}));

const scrollbarStyle = computed(() => ({
  width: `${100 / medias.length}%`,
  translate: `${scrollbarTranslate.value}% 0`,
}));

const clickLink = () => linkRef.value?.click();

const setSwiperInstance = (swiper: SwiperType) => {
  swiper.on('setTranslate', ({ width, rtlTranslate }, translate) => {
    const direction = rtlTranslate ? -1 : 1;
    scrollbarTranslate.value = direction * (Math.abs(translate) / width) * 100;
  });
};
</script>

<template>
  <div
    :class="[
      'phxsg-product-card',
      {
        'phxsg-product-card--horizontal': horizontal || horizontalOnly,
        'phxsg-product-card--horizontal-only': horizontalOnly,
        'phxsg-product-card--full-width-media': fullWidthMedia,
      },
    ]"
  >
    <div v-if="hasTags" class="phxsg-product-card__tags" data-cy="product-tags">
      <slot name="tags" :tags="tags">
        <PhxsgTag v-for="(tag, index) in tags" :key="`product-tag-${index}`">
          {{ tag }}
        </PhxsgTag>
      </slot>
    </div>

    <div class="phxsg-product-card__wrapper">
      <div class="phxsg-product-card__media">
        <PhxsgSlider
          v-if="hasMultipleMedias"
          class="phxsg-product-card__slider"
          :navigation="true"
          :rewind="true"
          :keyboard="false"
          :prev-button-label="previousSlideLabel"
          :next-button-label="nextSlideLabel"
          nav-slot="container-end"
          @swiper="setSwiperInstance"
        >
          <template #nav-prev="{ label }">
            <PhxsgButton
              class="phxsg-product-card__slider-nav phxsg-product-card__slider-nav--prev"
              :icon-only="true"
              :aria-label="label"
            >
              <template #content>
                <PhxsgIcon id="chevron-left" bidirectional />
              </template>
            </PhxsgButton>
          </template>

          <template #nav-next="{ label }">
            <PhxsgButton
              class="phxsg-product-card__slider-nav phxsg-product-card__slider-nav--next"
              :icon-only="true"
              :aria-label="label"
            >
              <template #content>
                <PhxsgIcon id="chevron-right" bidirectional />
              </template>
            </PhxsgButton>
          </template>

          <PhxsgSlide
            v-for="(media, index) in medias"
            :key="`product-media-${index}`"
            class="phxsg-product-card__slide"
            @click="clickLink"
          >
            <PhxsgPicture v-bind="media" :type="media.type ?? pictureType" />
          </PhxsgSlide>
        </PhxsgSlider>

        <PhxsgPicture v-else-if="primaryMedia" v-bind="primaryMedia" :type="primaryMedia.type ?? pictureType" />
      </div>

      <div class="phxsg-product-card__content">
        <div v-if="name" class="phxsg-product-card__title body--weight-medium">
          <a v-if="href" ref="linkRef" class="expand-target" :href="href" v-bind="linkAttrs" @click="emit('click')">
            <slot name="title" :product-name="name">
              <span class="phxsg-product-card__title-text">{{ name }}</span>
            </slot>
          </a>
          <slot v-else name="title" :product-name="name">
            <span class="phxsg-product-card__title-text">{{ name }}</span>
          </slot>
        </div>

        <div v-if="hasPriceContent" class="phxsg-product-card__price max-sm:title-caption md:body--weight-medium">
          <slot name="price" :price="price">
            {{ price }}
          </slot>
        </div>

        <div v-if="hasReferenceContent" class="phxsg-product-card__ref title-caption">
          <slot name="reference" :product-ref="productRef">
            {{ productRef }}
          </slot>
        </div>
      </div>
    </div>

    <slot />

    <div v-if="hasActionsContent" class="phxsg-product-card__actions">
      <slot name="actions" />
    </div>

    <template v-if="hasMultipleMedias">
      <div class="phxsg-product-card__slider-scrollbar-back"></div>
      <div class="phxsg-product-card__slider-scrollbar" :style="scrollbarStyle"></div>
    </template>
  </div>
</template>

<style src="./product-card.css" />
